import BreadCrump from "../tools/breadcrump";
import { getCookies } from "cookies-next";
import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from 'sweetalert2';
import { config } from "../../data/config";
import { useTranslation } from 'react-i18next';
import HeaderOfPage from "../tools/headerOfPage";
import { Button, Input } from 'rsuite';
import { useNavigate } from 'react-router-dom';

export default function ListTickets() {
    const { t } = useTranslation();
    const [key, setKey] = useState(1);

    const breadcrump = [
        {
            name: 'Dashboard',
            url: '/dashboard'
        },
        {
            name: 'Tickets',
            url: '/dashboard/Tickets'
        },
        {
            name: 'List Tickets',
            url: null
        },
    ];

    const reloadgame = () => {
        let x = key + 1;
        setKey(x);
    };

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump}></BreadCrump>
            <HeaderOfPage name="List Tickets"></HeaderOfPage>
            <div className="mt-6">
                <ListofTickets key={key} reloaditem={reloadgame}></ListofTickets>
            </div>
        </div>
    );
}

function ListofTickets({ reloaditem }: { reloaditem: () => void }) {
    const { t } = useTranslation();
    let cookie = getCookies();
    const [list, setList] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [statusFilter, setStatusFilter] = useState<string>('all'); // all, reviewing, answered, closed

    const load = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: currentPage.toString(),
            });
            
            if (statusFilter !== 'all') {
                // Map frontend status to backend status codes
                const statusMap: { [key: string]: string } = {
                    'reviewing': '1',
                    'answered': '2',
                    'closed': '3'
                };
                queryParams.append('status', statusMap[statusFilter] || statusFilter);
            }

            const response = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/tickets?${queryParams.toString()}`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            setList(response.data.message);
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error loading tickets'),
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [currentPage, statusFilter]);

    const handleStatusChange = async (id: string, status: string) => {
        try {
            await axios({
                method: 'put',
                url: `${config.url}/v1/dashboard/tickets/${id}`,
                data: { status },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Ticket status updated'),
                timer: 1500,
                showConfirmButton: false
            });

            reloaditem();
            load();
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error updating ticket'),
            });
        }
    };

    const handleDelete = async (id: string) => {
        const result = await Swal.fire({
            title: t('Are you sure?'),
            text: t('You will not be able to recover this ticket!'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        });

        if (result.isConfirmed) {
            try {
                await axios({
                    method: 'delete',
                    url: `${config.url}/v1/dashboard/tickets/${id}`,
                    headers: {
                        Authorization: 'Bearer ' + cookie['token'],
                    }
                });

                Swal.fire({
                    icon: 'success',
                    title: t('Deleted!'),
                    text: t('Ticket has been deleted.'),
                    timer: 1500,
                    showConfirmButton: false
                });

                reloaditem();
                load();
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: t('Error'),
                    text: error.response?.data?.message || t('Error deleting ticket'),
                });
            }
        }
    };

    const handleAddReply = async (id: string, replyText: string, replyName: string) => {
        try {
            await axios({
                method: 'put',
                url: `${config.url}/v1/dashboard/tickets/${id}`,
                data: {
                    message: replyText,
                    name: replyName || 'Admin'
                },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Reply added successfully'),
                timer: 1500,
                showConfirmButton: false
            });

            reloaditem();
            load();
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error adding reply'),
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'answered':
                return 'bg-green-100 text-green-800';
            case 'reviewing':
                return 'bg-yellow-100 text-yellow-800';
            case 'closed':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'answered':
                return t('Answered');
            case 'reviewing':
                return t('Reviewing');
            case 'closed':
                return t('Closed');
            default:
                return status;
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Filters */}
            <div className="mb-6 flex gap-4 items-center">
                <label className="text-sm font-medium text-gray-700">{t('Filter')}:</label>
                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">{t('All Tickets')}</option>
                    <option value="reviewing">{t('Reviewing')}</option>
                    <option value="answered">{t('Answered')}</option>
                    <option value="closed">{t('Closed')}</option>
                </select>
            </div>

            {loading ? (
                <div className="text-center py-8">
                    <p className="text-gray-500">{t('Loading...')}</p>
                </div>
            ) : list && list.docs && list.docs.length > 0 ? (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('User')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('Title')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('Messages')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('Status')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('Date')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('Actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {list.docs.map((item: any) => (
                                    <TicketRow
                                        key={item.id || item._id}
                                        item={item}
                                        onStatusChange={handleStatusChange}
                                        onDelete={handleDelete}
                                        onAddReply={handleAddReply}
                                        getStatusColor={getStatusColor}
                                        getStatusText={getStatusText}
                                        t={t}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {list.totalPages > 1 && (
                        <div className="mt-6 flex justify-center gap-2">
                            <Button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(currentPage - 1)}
                            >
                                {t('Previous')}
                            </Button>
                            <span className="px-4 py-2">
                                {t('Page')} {currentPage} {t('of')} {list.totalPages}
                            </span>
                            <Button
                                disabled={currentPage === list.totalPages}
                                onClick={() => setCurrentPage(currentPage + 1)}
                            >
                                {t('Next')}
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-8">
                    <p className="text-gray-500">{t('No tickets found')}</p>
                </div>
            )}
        </div>
    );
}

function TicketRow({ item, onStatusChange, onDelete, onAddReply, getStatusColor, getStatusText, t }: any) {
    const navigate = useNavigate();
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [replyName, setReplyName] = useState('Admin');

    const userName = item.user 
        ? `${item.user.name || ''} ${item.user.lastname || ''}`.trim() || 'Guest'
        : 'Guest';

    const userEmail = item.user?.email || '';
    const userPhone = item.user?.phone || '';

    return (
        <tr 
            className="hover:bg-gray-50 cursor-pointer"
            onClick={() => navigate(`/dashboard/Tickets/${item.id || item._id}`)}
        >
                <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{userName}</div>
                {userEmail && (
                    <div className="text-sm text-gray-500">{userEmail}</div>
                )}
                {userPhone && (
                    <div className="text-sm text-gray-500">{userPhone}</div>
                )}
            </td>
            <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">{item.title}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                    {(() => {
                        const replyCount = item.replies ? item.replies.length : (item.supportResponse ? 1 : 0);
                        return `${replyCount} ${replyCount === 1 ? t('Message') : t('Messages')}`;
                    })()}
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                    {getStatusText(item.status)}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                    {new Date(item.date).toLocaleDateString()}
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                    <select
                        value={item.status}
                        onChange={(e) => {
                            e.stopPropagation();
                            onStatusChange(item.id || item._id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                        <option value="reviewing">{t('Reviewing')}</option>
                        <option value="answered">{t('Answered')}</option>
                        <option value="closed">{t('Closed')}</option>
                    </select>
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="sm"
                            color="blue"
                            onClick={(e: any) => {
                                e.stopPropagation();
                                setShowReplyForm(!showReplyForm);
                            }}
                        >
                            {item.supportResponse ? t('Edit Reply') : t('Add Reply')}
                        </Button>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="sm"
                            color="red"
                            onClick={(e: any) => {
                                e.stopPropagation();
                                onDelete(item.id || item._id);
                            }}
                        >
                            {t('Delete')}
                        </Button>
                    </div>
                </div>
                {showReplyForm && (
                    <div 
                        className="mt-4 p-4 bg-gray-50 rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Input
                            placeholder={t('Reply Name')}
                            value={replyName}
                            onChange={(val) => setReplyName(val)}
                            className="mb-2"
                        />
                        <textarea
                            placeholder={t('Reply Text')}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            className="w-full p-2 border rounded mb-2"
                            rows={3}
                        />
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                color="green"
                                onClick={() => {
                                    if (replyText.trim()) {
                                        onAddReply(item.id || item._id, replyText, replyName);
                                        setReplyText('');
                                        setShowReplyForm(false);
                                    }
                                }}
                            >
                                {t('Save Reply')}
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => {
                                    setShowReplyForm(false);
                                    setReplyText('');
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                        </div>
                    </div>
                )}
            </td>
        </tr>
    );
}

